/**
 * Query Tool tokens, one per project (docs/query-tool/token-contract.md). CENTERLINE's backend
 * issues them for 15 minutes; a cached token is used until 60 seconds before it expires, and
 * concurrent callers share one request for the same project.
 */

export interface IssuedToken {
  token: string;
  /** ISO time, or epoch milliseconds. */
  expiresAt: string | number;
  /**
   * Seconds until it expires. When present it wins: the expiry is counted from the moment the
   * token arrives, so a browser clock that is off cannot make a fresh token look stale (or a stale
   * one fresh).
   */
  expiresIn?: number;
}

export interface TokenCache {
  /** A usable token for the project. `forceRefresh` discards the cached one (after a 401). */
  get(projectId: string, options?: { forceRefresh?: boolean }): Promise<string>;
  clear(): void;
}

export const TOKEN_REFRESH_MARGIN_MS = 60 * 1000;

const expiryMs = (expiresAt: string | number): number => {
  const ms = typeof expiresAt === 'number' ? expiresAt : Date.parse(expiresAt);
  // An expiry that cannot be read is treated as already past: the token is still used for the
  // request that fetched it, but never served from the cache.
  return Number.isFinite(ms) ? ms : 0;
};

export function createTokenCache(
  issue: (projectId: string) => Promise<IssuedToken>,
  options: { now?: () => number; refreshMarginMs?: number } = {},
): TokenCache {
  const now = options.now ?? (() => Date.now());
  const margin = options.refreshMarginMs ?? TOKEN_REFRESH_MARGIN_MS;
  const cached = new Map<string, { token: string; expiresAtMs: number }>();
  const inFlight = new Map<string, Promise<string>>();
  // Bumped by clear(), so a request that started before it cannot repopulate the cache.
  let generation = 0;

  const fetchToken = (projectId: string): Promise<string> => {
    const started = generation;
    const request = issue(projectId)
      .then((issued) => {
        if (started === generation) {
          cached.set(projectId, {
            token: issued.token,
            expiresAtMs:
              typeof issued.expiresIn === 'number' && Number.isFinite(issued.expiresIn)
                ? now() + issued.expiresIn * 1000
                : expiryMs(issued.expiresAt),
          });
        }
        return issued.token;
      })
      .finally(() => {
        if (inFlight.get(projectId) === request) inFlight.delete(projectId);
      });
    inFlight.set(projectId, request);
    return request;
  };

  return {
    get(projectId, { forceRefresh = false } = {}) {
      const pending = inFlight.get(projectId);
      // A request already on its way is fresh by definition, so even a forced refresh joins it.
      if (pending) return pending;
      const entry = cached.get(projectId);
      if (!forceRefresh && entry && now() < entry.expiresAtMs - margin) {
        return Promise.resolve(entry.token);
      }
      cached.delete(projectId);
      return fetchToken(projectId);
    },
    clear() {
      generation += 1;
      cached.clear();
      inFlight.clear();
    },
  };
}
